import { useCallback, useRef, useState, useEffect } from 'react';
import * as ActionCable from '@rails/actioncable';
import { ChatBotService } from '~/training/sequence/templates/items/chatBot/services/chatBotService';
import { useTrainingStore } from '~/training/state';

export interface MessageData {
  id?: number | string | undefined;
  body?: string | null;
  role: 'user' | 'assistant';
  created_at?: string;
  updated_at?: string;
  isError?: boolean;
}

interface UseWebSocketChatProps {
  chatBotService: ChatBotService | null;
  disableAutoConversationCreation?: boolean;
}

interface UseWebSocketChatReturn {
  messages: MessageData[];
  loading: boolean;
  conversationId: string | null;
  sendMessage: (
    text: string,
    subject?: string,
    showUserMessage?: boolean
  ) => Promise<void>;
  resetConversation: () => Promise<void>;
  addUserMessage: (text: string) => void;
  sendInitialMessage: (subject?: string) => Promise<void>;
}

const generateUniqueId = (): string => {
  const timestamp = Date.now();
  const randomPart = Math.random().toString(36).substring(2) || '0';
  return `${timestamp}-${randomPart}`;
};

const createErrorMessage = (message?: string): MessageData => ({
  id: generateUniqueId(),
  body:
    message ||
    'Error. Server Not Responding\n\n❌ Chat server is not responding try again later or you can skip this role-play',
  role: 'assistant',
  isError: true
});

export const useWebSocketChat = ({
  chatBotService
}: UseWebSocketChatProps): UseWebSocketChatReturn => {
  const cableRef = useRef<ActionCable.Consumer | null>(null);
  const subscriptionRef = useRef<ActionCable.Subscription | null>(null);
  const subscriptionReadyRef = useRef<boolean>(false);
  const intervalsRef = useRef<Set<NodeJS.Timeout>>(new Set());
  const initialMessageSentRef = useRef<boolean>(false);
  const conversationCreatingRef = useRef<boolean>(false);

  const [messages, setMessages] = useState<MessageData[]>([]);
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const textLocale = useTrainingStore(s => s.textLocale);

  const waitForConnection = useCallback(async () => {
    if (subscriptionReadyRef.current) {
      return;
    }
    const maxMs = 500;
    const startedAt = Date.now();

    return new Promise<void>(resolve => {
      const interval = setInterval(() => {
        const elapsed = Date.now() - startedAt;

        if (subscriptionReadyRef.current) {
          clearInterval(interval);
          intervalsRef.current.delete(interval);
          resolve();
        } else if (elapsed > maxMs) {
          clearInterval(interval);
          intervalsRef.current.delete(interval);
          resolve();
        }
      }, 50);
      intervalsRef.current.add(interval);
    });
  }, []);

  const createConversation = useCallback(async (): Promise<string> => {
    if (!chatBotService) {
      return '';
    }

    if (conversationId) {
      return conversationId;
    }

    if (conversationCreatingRef.current) {
      return new Promise(resolve => {
        const checkInterval = setInterval(() => {
          if (!conversationCreatingRef.current && conversationId) {
            clearInterval(checkInterval);
            resolve(conversationId);
          }
        }, 10);

        setTimeout(() => {
          clearInterval(checkInterval);
          resolve('');
        }, 5000);
      });
    }

    conversationCreatingRef.current = true;

    try {
      const data = await chatBotService.createChatbotConversation();

      if (data.error) {
        conversationCreatingRef.current = false;
        return '';
      }

      setConversationId(data.conversation_id);

      try {
        const wsUrl = chatBotService.getWebSocketUrl();

        if (!cableRef.current) {
          cableRef.current = ActionCable.createConsumer(wsUrl);
        }

        if (subscriptionRef.current) {
          const currentIdentifier = subscriptionRef.current.identifier;
          const newIdentifier = JSON.stringify({
            channel: 'ConversationChannel',
            conversation_id: data.conversation_id
          });

          if (currentIdentifier === newIdentifier) {
            return data.conversation_id as string;
          }

          subscriptionRef.current.unsubscribe();
          subscriptionRef.current = null;
        }

        const subscriptionParams = {
          channel: 'ConversationChannel',
          conversation_id: data.conversation_id
        };

        try {
          subscriptionRef.current = cableRef.current.subscriptions.create(
            subscriptionParams,
            {
              connected: () => {
                subscriptionReadyRef.current = true;
              },
              disconnected: () => {
                subscriptionReadyRef.current = false;
              },
              rejected: () => {
                subscriptionReadyRef.current = false;
              },
              received: (data: unknown) => {
                const evt = (data as any)?.event;
                const payload = (data as any)?.data || {};

                switch (evt) {
                  case 'message': {
                    const message: MessageData = {
                      id: generateUniqueId(),
                      body: payload.text,
                      role: 'assistant'
                    };

                    setMessages(prev => {
                      const updated = [...prev];
                      const last = updated[updated.length - 1];
                      if (last && last.role === 'assistant') {
                        updated[updated.length - 1] = {
                          ...last,
                          body: payload.text
                        };
                      } else {
                        updated.push(message);
                      }
                      return updated;
                    });
                    break;
                  }

                  case 'message.done': {
                    const finalMessage: MessageData = {
                      id: generateUniqueId(),
                      body: payload.text,
                      role: 'assistant'
                    };

                    setMessages(prev => {
                      const updated = [...prev];
                      const last = updated[updated.length - 1];
                      if (last && last.role === 'assistant') {
                        updated[updated.length - 1] = {
                          ...last,
                          body: payload.text
                        };
                      } else {
                        updated.push(finalMessage);
                      }
                      return updated;
                    });

                    setLoading(false);
                    break;
                  }

                  case 'conversation-reset':
                    setConversationId(payload.conversation_id || null);
                    setMessages([]);
                    setLoading(false);
                    break;

                  case 'error': {
                    const errorMessage = createErrorMessage();
                    setMessages(prev => [...prev, errorMessage]);
                    setLoading(false);
                    break;
                  }

                  case 'ping':
                    break;

                  default:
                    break;
                }
              }
            }
          );
        } catch (subscriptionError) {
          subscriptionRef.current = null;
        }
      } catch (e) {
        // WebSocket setup failed
      }

      conversationCreatingRef.current = false;
      return data.conversation_id as string;
    } catch (error) {
      conversationCreatingRef.current = false;
      return '';
    }
  }, [chatBotService, conversationId]);

  const addUserMessage = useCallback((text: string) => {
    const userMessage: MessageData = {
      id: generateUniqueId(),
      body: text,
      role: 'user',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    setMessages(prev => [...prev, userMessage]);
  }, []);

  const sendMessage = useCallback(
    async (
      text: string,
      subject?: string,
      showUserMessage = true
    ): Promise<void> => {
      if (!text || text.trim().length < 1) {
        return;
      }

      setLoading(true);
      if (showUserMessage) {
        addUserMessage(text);
      }

      try {
        const conv = conversationId || (await createConversation());

        if (!conv) {
          throw new Error('Failed to create or retrieve conversation.');
        }

        if (subscriptionRef.current && subscriptionReadyRef.current) {
          try {
            subscriptionRef.current.perform('speak', {
              conversation_id: conv,
              text: text,
              bot: 'roleplay_assessor',
              subject
            });

            if (!conversationId) {
              setConversationId(conv);
            }
            return;
          } catch (wsError) {
            // WebSocket failed, fall through to REST API
          }
        }

        // REST API fallback
        if (chatBotService) {
          const data = await chatBotService.sendChatbotMessage(
            conv,
            text,
            subject
          );
          if (data && data.error) {
            throw new Error(data.error);
          }
          if (data && data.text) {
            const assistantMessage: MessageData = {
              id: generateUniqueId(),
              body: data.text,
              role: 'assistant'
            };
            setMessages(prev => [...prev, assistantMessage]);
          }
          setLoading(false);
        }

        if (!conversationId) {
          setConversationId(conv);
        }
      } catch (e) {
        const errorMessage = createErrorMessage();
        setMessages(prev => [...prev, errorMessage]);
        setLoading(false);

        throw new Error(e);
      }
    },
    [
      chatBotService,
      conversationId,
      createConversation,
      waitForConnection,
      addUserMessage
    ]
  );

  const sendInitialMessage = useCallback(
    async (subject?: string): Promise<void> => {
      if (initialMessageSentRef.current) {
        return;
      }
      initialMessageSentRef.current = true;

      setLoading(true);

      try {
        let conv = conversationId;

        if (!conv) {
          conv = await createConversation();
          if (!conv) {
            const errorMessage = createErrorMessage();
            setMessages(prev => [...prev, errorMessage]);
            setLoading(false);
            return;
          }
          setConversationId(conv);
        }

        const greeting =
          textLocale && textLocale.toLowerCase() !== 'en'
            ? `hi <userLangSelected>${textLocale}</userLangSelected>`
            : 'Hi';
        await sendMessage(greeting, subject, false);
      } catch (e) {
        const errorMessage = createErrorMessage();
        setMessages(prev => [...prev, errorMessage]);
        setLoading(false);
      }
    },
    [
      chatBotService,
      conversationId,
      createConversation,
      waitForConnection,
      textLocale
    ]
  );

  const resetConversation = useCallback(async () => {
    setMessages([]);
    setLoading(false);

    try {
      if (subscriptionRef.current && conversationId) {
        subscriptionRef.current.perform('reset', {
          conversation_id: conversationId
        });
      } else if (chatBotService) {
        const data = await chatBotService.resetChatbotConversation();
        setConversationId(data.conversation_id || null);
      }
    } catch (e) {
      // ignore reset errors for now
    }
  }, [chatBotService, conversationId]);

  useEffect(() => {
    return () => {
      intervalsRef.current.forEach(interval => clearInterval(interval));
      intervalsRef.current.clear();

      conversationCreatingRef.current = false;

      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
        subscriptionRef.current = null;
      }

      if (cableRef.current) {
        cableRef.current.disconnect();
        cableRef.current = null;
      }

      subscriptionReadyRef.current = false;
    };
  }, [chatBotService]);

  return {
    messages,
    loading,
    conversationId,
    sendMessage,
    resetConversation,
    addUserMessage,
    sendInitialMessage
  };
};
